import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import dotersLogo from 'images/doters/greendoters.svg';
import travelpassLogo from 'images/travelpass/travelpass-logo.png';
import coinGif from 'images/coin.gif';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

// Brand logos imports
import defaultBus from 'images/utilities/bus_loading.png';
import gfaLogo from 'images/brands/gfa/utilities/loading-1.png';
import atnLogo from 'images/brands/atn/utilities/loading-1.png';
import brasiliaLogo from 'images/brands/brasilia/utilities/loading-1.png';
import costalineLogo from 'images/brands/costaline/utilities/loading-1.png';
import demoLogo from 'images/brands/demo/utilities/loading-1.png';
import etnLogo from 'images/brands/etn/utilities/loading-1.png';
import fypsaLogo from 'images/brands/fypsa/utilities/loading-1.png';
import ghoLogo from 'images/brands/gho/utilities/loading-1.png';
import greyhoundLogo from 'images/brands/greyhound/utilities/loading-1.png';
import mybusLogo from 'images/brands/mybus/utilities/loading-1.png';
import ochoaLogo from 'images/brands/ochoa/utilities/loading-1.png';
import rollbitsLogo from 'images/brands/rollbits/utilities/loading-1.png';
import talpaLogo from 'images/brands/talpa/utilities/loading-1.png';
import vencedorLogo from 'images/brands/vencedor/utilities/loading-1.png';
import useLoyaltyPrograms from '../loyalty/context/useLoyaltyPrograms';

const brandLogos = {
  default: defaultBus,
  gfa: gfaLogo,
  atn: atnLogo,
  brasilia: brasiliaLogo,
  costaline: costalineLogo,
  demo: demoLogo,
  etn: etnLogo,
  fypsa: fypsaLogo,
  gho: ghoLogo,
  greyhound: greyhoundLogo,
  mybus: mybusLogo,
  ochoa: ochoaLogo,
  rollbits: rollbitsLogo,
  talpa: talpaLogo,
  vencedor: vencedorLogo,
};

/**
 * LoadingWallet component.
 * Render a loading message with information about digital wallet benefits.
 *
 * @param {object} props - showText.
 * @param {boolean} props.showText - Optional: Whether to show the text message. Defaults to true.
 * @returns {JSX.Element} The rendered LoadingWallet component.
 */
const LoadingWallet = ({ showText = true }) => {
  const { t } = useTranslation('payment');
  const { brand } = useWhitelabelEnvs();

  // guard selector in case tests provide undefined shape
  const { features = {} } = useSelector(
    (state) => (state && state.whitelabelConfig) || { features: {} },
  );

  const [fade, setFade] = useState(false);

  // safe extraction with defaults
  const loyalty = useLoyaltyPrograms() || {};
  const userIsLoggedInWithCostaPass = loyalty?.costaPass?.userIsLoggedIn ?? false;
  const userIsLoggedInWithDoters = loyalty?.doters?.userIsLoggedIn ?? false;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const brandLogo = brandLogos[brand] ?? brandLogos.default;

  // Calculate enableDoters: show if user IS logged in with Doters and feature is enabled
  const enableDoters = features.DOTERS_ENABLED && userIsLoggedInWithDoters;
  // Calculate enableCostapass: show if user IS logged in with CostaPass and feature is enabled
  const enableCostapass = features.COSTAPASS_ENABLED && userIsLoggedInWithCostaPass;

  const texts = useMemo(
    () => [t('payment_almost_complete'), t('almost_ready'), t('finishing_payment_process')],
    [t],
  );

  const states = useMemo(() => {
    const allStates = [
      {
        image: brandLogo,
        bgColor: 'bg-primary-regular',
      },
      ...(enableDoters
        ? [
            {
              image: dotersLogo,
              bgColor: 'bg-[var(--loyalty)]',
            },
          ]
        : []),
      ...(enableCostapass
        ? [
            {
              image: travelpassLogo,
              bgColor: 'bg-[#2A78EC]',
            },
          ]
        : []),
    ];
    return allStates;
  }, [enableDoters, enableCostapass, brandLogo]);

  useEffect(() => {
    setCurrentImageIndex((prevIndex) => Math.min(prevIndex, states.length - 1));
    setCurrentTextIndex((prevIndex) => Math.min(prevIndex, texts.length - 1));
  }, [states.length, texts.length]);

  useEffect(() => {
    let fadeTimeoutId;

    const imageTimer = setInterval(() => {
      setFade(true); // start fade-out
      fadeTimeoutId = setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % states.length);
        setFade(false); // fade back in
      }, 500); // FADE_OUT_DURATION
    }, 2700); // IMAGE_TRANSITION_INTERVAL

    return () => {
      clearInterval(imageTimer);
      if (fadeTimeoutId) {
        clearTimeout(fadeTimeoutId);
      }
    };
  }, [states.length]);

  useEffect(() => {
    const textTimer = setInterval(() => {
      setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
    }, 2700); // TEXT_TRANSITION_INTERVAL

    return () => clearInterval(textTimer);
  }, [texts.length]);

  const currentState = states[currentImageIndex] || states[0];
  const currentText = texts[currentTextIndex] || texts[0];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 bg-gray-100 bg-opacity-85 backdrop-blur-sm flex flex-col items-center justify-center z-50"
    >
      <div className="relative flex items-center justify-center w-80 h-48">
        <img src={coinGif} alt="" className="w-12 h-12 absolute top-3 right-8 z-10" />
        <div
          className={`w-40 h-24 rounded-3xl origin-bottom-left
            animate-[rotateUp_2750ms_ease-in-out_infinite] gap-4
            flex items-center justify-center
            transition-[background-color_500ms]
            ${currentState.bgColor}
            relative overflow-hidden`}
        >
          <div
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
              fade ? 'opacity-0' : 'opacity-100'
            }`}
          >
            <img
              src={currentState.image}
              alt=""
              className="absolute w-36 h-36 opacity-10 pointer-events-none top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            />

            <img src={currentState.image} alt="" className="w-16 h-16 relative z-10" />
          </div>
        </div>
        <div className="absolute left-17 w-40 h-24 bg-gray-100/20 backdrop-blur-sm rounded-3xl outline outline-5 outline-gray-200 animate-[rotateDown_2750ms_ease-in-out_infinite] origin-bottom-left">
          <div className="absolute top-4 left-3 w-12 h-2 bg-white rounded-md" />
          <div className="absolute top-8 -right-1.5 w-20 h-9 bg-primary-regular rounded-l-xl flex items-center justify-center">
            <div className="w-4 h-4 left-3 absolute bg-white rounded-full" />
          </div>
        </div>
      </div>
      {showText && (
        <p
          key={currentTextIndex}
          className="text-gray-600 text-center h-12 text-sm px-4 animate-[fade-in_0.5s_ease-in-out]"
        >
          {currentText}
        </p>
      )}
    </div>
  );
};

LoadingWallet.propTypes = {
  showText: PropTypes.bool,
};

export default LoadingWallet;
